import { Injectable, Injector } from '@angular/core';
import { ActionSheetController, AlertController, ModalController, ToastController } from '@ionic/angular';
import { LoadingService } from '../loading-service/loading.service';
import { VivaInfoPopupComponent } from 'src/app/controls/viva-info-popup/viva-info-popup.component';
import { TimeoutError } from 'rxjs';
import { VivaErrorPopupComponent } from 'src/app/controls/viva-error-popup/viva-error-popup.component';
import { VivaWarningPopupComponent } from 'src/app/controls/viva-warning-popup/viva-warning-popup.component';
import { VivaPermissionPopupComponent } from 'src/app/controls/viva-permission-popup/viva-permission-popup.component';
import { SystemLogService } from '../systemlog-service/systemlog.service';
import { TranslatorService } from '../translator-service/translator.service';

/** Ids for {@link PromptService.presentPromptTestMenu} / {@link PromptService.runPromptUiSample}. */
export type PromptUiSampleId =
  | 'error'
  | 'info'
  | 'warning'
  | 'confirmation'
  | 'toast'
  | 'permission'
  | 'throw-error'
  | 'network-error';

@Injectable({
  providedIn: 'root',
})
export class PromptService {

  constructor(protected _alertController: AlertController,
    private _toast: ToastController,
    protected modalController: ModalController,
    private _actionSheet: ActionSheetController,
    /** Lazy resolve TranslatorService — constructor inject creates NG0200 with AuthService. */
    private _injector: Injector,
  ) { }

  private translateTest(key: string): string {
    return this._injector.get(TranslatorService).translateText(`prompt-service.test.${key}`);
  }

  private translatePrompt(key: 'yes' | 'no'): string {
    return this._injector.get(TranslatorService).translateText(`prompt-service.${key}`);
  }
  public async throwError(
    errorObject: any,
    statusText: string,
    title: string = 'Error'
  ) {
    SystemLogService.logError(errorObject, statusText, title);

    await this.showError(statusText);

    await LoadingService.closeAllLoaders();

  }


  /**
   * Reads a user-facing message from an Angular HttpClient failure (HttpErrorResponse) or similar.
   * Handles JSON bodies like `{ message: "..." }` and string bodies that parse as JSON.
   */
  public tryGetServerErrorMessage(error: unknown): string | null {
    if (error == null) {
      return null;
    }
    const err = error as { error?: unknown; message?: string; status?: number; statusText?: string };
    const body = err.error;

    if (typeof body === 'string') {
      const trimmed = body.trim();
      if (!trimmed) {
        return this.httpStatusFallback(err.status, err.statusText);
      }
      try {
        const parsed = JSON.parse(trimmed) as {
          message?: unknown;
          errors?: unknown;
          error?: unknown;
          error_description?: unknown;
          title?: unknown;
        };
        const fromParsed = this.messageFromErrorBody(parsed);
        if (fromParsed) {
          return fromParsed;
        }
      } catch {
        return trimmed;
      }
      return this.httpStatusFallback(err.status, err.statusText);
    }

    if (body && typeof body === 'object') {
      const fromBody = this.messageFromErrorBody(body as Record<string, unknown>);
      if (fromBody) {
        return fromBody;
      }
    }

    if (typeof err.message === 'string' && err.message.trim() && !err.message.startsWith('Http failure')) {
      return err.message.trim();
    }

    return this.httpStatusFallback(err.status, err.statusText);
  }

  /** Prefer API message fields; fall back to HTTP status text for debugging on device. */
  public formatRegistrationFailureDetail(error: unknown, fallback: string): string {
    const serverMessage = this.tryGetServerErrorMessage(error);
    if (serverMessage) {
      return serverMessage;
    }
    if (error instanceof TimeoutError) {
      return fallback;
    }
    const err = error as { status?: number; statusText?: string; message?: string };
    return this.httpStatusFallback(err?.status, err?.statusText) ?? fallback;
  }

  private messageFromErrorBody(body: {
    message?: unknown;
    errors?: unknown;
    error?: unknown;
    error_description?: unknown;
    title?: unknown;
  }): string | null {
    if (typeof body.message === 'string' && body.message.trim()) {
      return body.message.trim();
    }
    if (typeof body.error_description === 'string' && body.error_description.trim()) {
      return body.error_description.trim();
    }
    if (typeof body.title === 'string' && body.title.trim()) {
      return body.title.trim();
    }
    if (typeof body.error === 'string' && body.error.trim() && body.error.trim().toLowerCase() !== 'invalid_request') {
      return body.error.trim();
    }
    if (typeof body.error === 'string' && body.error.trim() && typeof body.error_description === 'string') {
      return `${body.error}: ${body.error_description}`.trim();
    }
    const fromErrors = this.firstValidationErrorMessage(body.errors);
    if (fromErrors) {
      return fromErrors;
    }
    return null;
  }

  private httpStatusFallback(status?: number, statusText?: string): string | null {
    if (status == null || status === 0) {
      return null;
    }
    const text = (statusText ?? '').trim();
    return text ? `Request failed (${status} ${text})` : `Request failed (HTTP ${status})`;
  }

  private firstValidationErrorMessage(errors: unknown): string | null {
    if (errors == null || typeof errors !== 'object') {
      return null;
    }
    for (const v of Object.values(errors as Record<string, unknown>)) {
      if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'string' && v[0].trim()) {
        return v[0].trim();
      }
      if (typeof v === 'string' && v.trim()) {
        return v.trim();
      }
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async throwNewtorkError(err: any) {
    try {
      const loggable = err != null ? err : 'Network connection failed (no error details)';
      SystemLogService.logError(loggable, 'Network error', 'Network error');
    } catch (er) {
      console.error(er);
    }

    await LoadingService.closeAllLoaders();

    const serverMessage = this.tryGetServerErrorMessage(err);
    if (serverMessage) {
      if (serverMessage.indexOf('Unknown') > 0) {
        // await this.showToastMessage(this._TranslatorService.translateText('prompt-service.server-error'));
      } else {
        await this.showError(serverMessage);
      }
    } else {
      // await this.showToastMessage(this._TranslatorService.translateText('prompt-service.server-not-reachable'));
    }
  }

  public async showError(err: string) {
    const modal = await this.modalController.create({
      component: VivaErrorPopupComponent,
      componentProps: {
        message: err
      },
      cssClass: 'custom-info-modal'
    });

    await modal.present();
    await modal.onDidDismiss();
  }

  public async showMessage(
    statusText: string,
    buttonText?: string
  ) {
    const modal = await this.modalController.create({
      component: VivaInfoPopupComponent,
      componentProps: {
        message: statusText,
        buttonText: buttonText || 'info-popup-component.button-ok'
      },
      cssClass: 'custom-info-modal'
    });

    await modal.present();
    await modal.onDidDismiss();
  }

  public async showPermissionPopup(
    statusText: string,
    animatedGif: string
  ) {
    const modal = await this.modalController.create({
      component: VivaPermissionPopupComponent,
      componentProps: {
        message: statusText,
        animatedGif: animatedGif,
        buttonText: 'info-popup-component.button-continue'
      },
      cssClass: 'custom-permission-modal'
    });

    await modal.present();
    await modal.onDidDismiss();
  }

  public async showToastMessage(message: string, duration: number = 2000) {
    const toast = await this._toast.create({
      message: message,
      duration: duration,
      position: 'bottom'
    });

    await toast.present();
  }

  private isShowingWarningMessage: boolean = false;
  public async showWarningMessage(
    message: string
  ) {

    if (this.isShowingWarningMessage)
      return;

    this.isShowingWarningMessage = true;
    try {

      const modal = await this.modalController.create({
        component: VivaWarningPopupComponent,
        componentProps: {
          message: message
        },
        cssClass: 'custom-info-modal'
      });

      await modal.present();
      await modal.onDidDismiss();
    } finally {
      this.isShowingWarningMessage = false;
    }

  }

  public async confirmationMessage(message: string, yesText?: string, noText?: string): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      const alert = await this._alertController.create({
        cssClass: 'custom-confirmation-alert',
        message: message,
        buttons: [
          {
            text: noText || this.translatePrompt('no'),
            role: 'cancel',
            handler: () => {
              resolve(false);
            }
          },
          {
            text: yesText || this.translatePrompt('yes'),
            handler: () => {
              resolve(true);
            }
          }
        ]
      });

      await alert.present();
      // No need to await onDidDismiss here since resolve is handled inside the button handlers
    });
  }


  private lastTimeout: Date | null = null;
  private resetTimout: any | null = null;

  public async showNetworkConnectionError(error: unknown, source: string, showGenericError: boolean = true) {
    if (error instanceof TimeoutError) {  // Ensure we check the instance type properly
      const now = new Date();
      const oneMinuteAgo = new Date(now.getTime() - 60 * 1000);  // 60,000 milliseconds = 1 minute

      if (this.lastTimeout === null || this.lastTimeout < oneMinuteAgo) {
        this.lastTimeout = now;
      }
    } else if (showGenericError) {
      await this.throwNewtorkError(error);
    }

    if (this.resetTimout) {
      clearTimeout(this.resetTimout);
      this.resetTimout = null;
    }
    this.resetTimout = setTimeout(() => {
      this.lastTimeout = null;
      if (this.resetTimout)
        clearTimeout(this.resetTimout);
      this.resetTimout = null;
    }, 60000 * 5);
  }

  /**
   * Testing helper: action sheet to preview each PromptService UI surface.
   * Call from a temporary / QA button (e.g. health score page).
   */
  public async presentPromptTestMenu(): Promise<void> {
    const sheet = await this._actionSheet.create({
      header: this.translateTest('menu-header'),
      cssClass: 'prompt-test-action-sheet',
      buttons: [
        { text: this.translateTest('run-all'), handler: () => { void this.runAllPromptUiSamples(); } },
        { text: this.translateTest('error'), handler: () => { void this.runPromptUiSample('error'); } },
        { text: this.translateTest('info'), handler: () => { void this.runPromptUiSample('info'); } },
        { text: this.translateTest('warning'), handler: () => { void this.runPromptUiSample('warning'); } },
        { text: this.translateTest('confirmation'), handler: () => { void this.runPromptUiSample('confirmation'); } },
        { text: this.translateTest('toast'), handler: () => { void this.runPromptUiSample('toast'); } },
        { text: this.translateTest('permission'), handler: () => { void this.runPromptUiSample('permission'); } },
        { text: this.translateTest('throw-error'), handler: () => { void this.runPromptUiSample('throw-error'); } },
        { text: this.translateTest('network-error'), handler: () => { void this.runPromptUiSample('network-error'); } },
        { text: this.translateTest('cancel'), role: 'cancel' },
      ],
    });
    await sheet.present();
  }

  /** Run one sample prompt UI by id. */
  public async runPromptUiSample(id: PromptUiSampleId): Promise<void> {
    switch (id) {
      case 'error':
        await this.showError(this.translateTest('sample-error'));
        break;
      case 'info':
        await this.showMessage(this.translateTest('sample-info'));
        break;
      case 'warning':
        await this.showWarningMessage(this.translateTest('sample-warning'));
        break;
      case 'confirmation': {
        const confirmed = await this.confirmationMessage(
          this.translateTest('sample-confirmation'),
          this.translateTest('sample-yes'),
          this.translateTest('sample-no'),
        );
        await this.showToastMessage(
          confirmed
            ? this.translateTest('sample-confirmation-yes')
            : this.translateTest('sample-confirmation-no'),
          2500,
        );
        break;
      }
      case 'toast':
        await this.showToastMessage(this.translateTest('sample-toast'), 3000);
        break;
      case 'permission':
        await this.showPermissionPopup(
          this.translateTest('sample-permission'),
          'assets/v3/apple-permissions.gif',
        );
        break;
      case 'throw-error':
        await this.throwError({ status: 500 }, this.translateTest('sample-throw-error'), 'Test');
        break;
      case 'network-error':
        await this.showNetworkConnectionError(
          {
            status: 503,
            statusText: 'Service Unavailable',
            error: { message: this.translateTest('sample-network-error') },
          },
          'prompt-ui-test',
          true,
        );
        break;
      default:
        break;
    }
  }

  /** Sequentially show every sample prompt (dismiss each to continue). */
  public async runAllPromptUiSamples(): Promise<void> {
    const order: PromptUiSampleId[] = [
      'error',
      'info',
      'warning',
      'confirmation',
      'toast',
      'permission',
      'throw-error',
      'network-error',
    ];
    for (const id of order) {
      await this.runPromptUiSample(id);
    }
  }

}